// src/components/InactivityMonitor.tsx

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, LogOut, RefreshCw } from 'lucide-react';
import { recordUserActivity, checkAndRefreshTokenIfActive, refreshAuthToken } from '@/lib/api-client';

const INACTIVITY_TIMEOUT = 20 * 60 * 1000;        // 20 minutes idle timeout
const WARNING_BEFORE = 60 * 1000;                 // show warning 60s before logout
const COUNTDOWN_START = WARNING_BEFORE / 1000;    // 60 seconds

export function InactivityMonitor() {
    const { isAuthenticated, logout } = useAuthStore();
    const navigate = useNavigate();

    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(COUNTDOWN_START);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const clearAllTimers = () => {
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        warningTimerRef.current = null;
        logoutTimerRef.current = null;
        countdownIntervalRef.current = null;
    };

    const handleLogout = useCallback(async () => {
        clearAllTimers();
        setShowWarning(false);
        try {
            await logout();
        } catch {
            // ignore
        }
        navigate('/login', { replace: true });
    }, [logout, navigate]);

    const stayLoggedIn = async () => {
        setIsRefreshing(true);
        try {
            await refreshAuthToken();
        } catch (err) {
            console.warn('[InactivityMonitor] stayLoggedIn refresh failed:', err);
        } finally {
            setIsRefreshing(false);
            clearAllTimers();
            setShowWarning(false);
            setCountdown(COUNTDOWN_START);
            recordUserActivity();
            resetTimers();
        }
    };

    const resetTimers = useCallback(() => {
        if (!isAuthenticated) return;
        recordUserActivity();
        clearAllTimers();

        // Set warning timer (fires 1 minute before idle logout)
        warningTimerRef.current = setTimeout(() => {
            setShowWarning(true);
            setCountdown(COUNTDOWN_START);

            // Start countdown interval
            countdownIntervalRef.current = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(countdownIntervalRef.current!);
                        handleLogout();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }, INACTIVITY_TIMEOUT - WARNING_BEFORE);

        // Set absolute fallback logout timer
        logoutTimerRef.current = setTimeout(() => {
            handleLogout();
        }, INACTIVITY_TIMEOUT);
    }, [isAuthenticated, handleLogout]);

    useEffect(() => {
        if (!isAuthenticated) {
            clearAllTimers();
            if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
            setShowWarning(false);
            return;
        }

        resetTimers();

        // Listen for all user interaction events to record activity & reset idle timers
        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
        const throttledReset = () => {
            recordUserActivity();
            // Only reset idle timers if the warning modal is not currently displayed
            if (!showWarning) {
                resetTimers();
            }
        };

        events.forEach((event) => window.addEventListener(event, throttledReset, { passive: true }));

        // Proactive token heartbeat: check every 60s if token needs refresh while user is active
        heartbeatIntervalRef.current = setInterval(() => {
            checkAndRefreshTokenIfActive(15);
        }, 60 * 1000);

        return () => {
            events.forEach((event) => window.removeEventListener(event, throttledReset));
            clearAllTimers();
            if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
        };
    }, [isAuthenticated, resetTimers, showWarning]);

    return (
        <AnimatePresence>
            {showWarning && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center border border-amber-100 dark:border-amber-900/40 text-slate-900 dark:text-slate-100"
                    >
                        <div className="w-16 h-16 rounded-2xl bg-amber-100/80 dark:bg-amber-950/60 flex items-center justify-center mx-auto mb-4 text-amber-600 dark:text-amber-400 shadow-inner">
                            <AlertTriangle size={32} />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-1">
                            Session Timeout Warning
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">
                            You have been inactive for a while.
                            <br />
                            Your session will automatically expire in{' '}
                            <span className="font-bold text-rose-600 tabular-nums">{countdown} seconds</span>.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={stayLoggedIn}
                                disabled={isRefreshing}
                                className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-emerald-600/20 hover:from-emerald-700 hover:to-teal-700 transition-all flex items-center justify-center gap-1.5"
                                type="button"
                            >
                                {isRefreshing ? (
                                    <>
                                        <RefreshCw size={14} className="animate-spin" />
                                        Refreshing...
                                    </>
                                ) : (
                                    'Stay Logged In'
                                )}
                            </button>
                            <button
                                onClick={handleLogout}
                                disabled={isRefreshing}
                                className="flex-1 py-3 border-2 border-slate-200 dark:border-slate-700 rounded-xl font-semibold text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex items-center justify-center gap-1"
                                type="button"
                            >
                                <LogOut size={16} />
                                Logout
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}